import React, { useState } from 'react';
import {
  Paper, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Typography, Button
} from '@mui/material';
import { Download, Cancel } from '@mui/icons-material';
import { toast } from 'react-toastify';

const VerificationResults = ({ results }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.info('No data to download');
      return;
    }

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  const getMatchTypeColor = (matchType) => {
    switch (matchType) {
      case 'EXACT':
        return 'success';
      case 'FLEXIBLE':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'FOUND':
        return 'success';
      case 'NOT_FOUND':
        return 'warning';
      case 'AMOUNT_MISMATCH':
        return 'error';
      case 'EXTRA_IN_DB':
        return 'info';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper elevation={2}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Found (${results.summary?.found_in_db || 0})`} />
        <Tab label={`Missing (${results.summary?.missing_from_db || 0})`} />
        <Tab label={`Duplicates (${results.summary?.duplicates_in_csv || 0})`} />
        <Tab label={`Errors (${results.summary?.errors || 0})`} />
      </Tabs>

      {/* Found Records Tab */}
      {activeTab === 0 && (
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Found Records</Typography>
            <Button
              startIcon={<Download />}
              onClick={() => downloadCSV(results.found_records, 'found_records.csv')}
              disabled={!results.found_records || results.found_records.length === 0}
            >
              Download
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row #</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>CSV Amount</TableCell>
                  <TableCell>DB Amount</TableCell>
                  <TableCell>Difference</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>DB Transaction ID</TableCell>
                  <TableCell>Transaction Type</TableCell>
                  <TableCell>Match Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.found_records?.map((record, index) => (
                  <TableRow key={index} sx={{ backgroundColor: record.status === 'AMOUNT_MISMATCH' ? '#ffebee' : 'inherit' }}>
                    <TableCell>{record.row_number}</TableCell>
                    <TableCell>{record.group}</TableCell>
                    <TableCell>{record.subscription_no}</TableCell>
                    <TableCell>₹{record.csv_amount?.toFixed(2)}</TableCell>
                    <TableCell>₹{record.db_amount?.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: record.amount_difference !== 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                      ₹{record.amount_difference?.toFixed(2)}
                    </TableCell>
                    <TableCell>{record.transaction_date}</TableCell>
                    <TableCell>{record.db_transaction_id}</TableCell>
                    <TableCell>
                      <Chip label={record.db_transaction_type} size="small" color="default" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={record.match_type} 
                        color={getMatchTypeColor(record.match_type)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={record.status} 
                        color={getStatusColor(record.status)} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {results.note && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
              {results.note}
            </Typography>
          )}
        </Box>
      )}

      {/* Missing Records Tab */}
      {activeTab === 1 && (
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Missing Records</Typography>
            <Button
              startIcon={<Download />}
              onClick={() => downloadCSV(results.missing_records, 'missing_records.csv')}
              disabled={!results.missing_records || results.missing_records.length === 0}
            >
              Download
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row #</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>CSV Amount</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.missing_records?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.row_number}</TableCell>
                    <TableCell>{record.group}</TableCell>
                    <TableCell>{record.subscription_no}</TableCell>
                    <TableCell>₹{record.csv_amount?.toFixed(2)}</TableCell>
                    <TableCell>{record.transaction_date || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={record.status} 
                        color={getStatusColor(record.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{record.reason || 'Not found in database'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Duplicates Tab */}
      {activeTab === 2 && (
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Duplicate Records</Typography>
            <Button
              startIcon={<Download />}
              onClick={() => downloadCSV(results.duplicate_records, 'duplicate_records.csv')}
              disabled={!results.duplicate_records || results.duplicate_records.length === 0}
            >
              Download
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row #</TableCell>
                  <TableCell>Duplicate of Row</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.duplicate_records?.map((record, index) => (
                  <TableRow key={index} sx={{ backgroundColor: '#fff3e0' }}>
                    <TableCell>{record.row_number}</TableCell>
                    <TableCell>
                      <Chip label={record.duplicate_of_row} size="small" color="warning" />
                    </TableCell>
                    <TableCell>{record.group}</TableCell>
                    <TableCell>{record.subscription_no}</TableCell>
                    <TableCell>₹{record.amount?.toFixed(2)}</TableCell>
                    <TableCell>{record.transaction_date}</TableCell>
                    <TableCell>
                      <Chip label={record.status} color="warning" size="small" />
                    </TableCell>
                    <TableCell>{record.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Error Records Tab */}
      {activeTab === 3 && (
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Error Records</Typography>
            <Button
              startIcon={<Download />}
              onClick={() => downloadCSV(results.error_records, 'error_records.csv')}
              disabled={!results.error_records || results.error_records.length === 0}
            >
              Download
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row #</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>Error Source</TableCell>
                  <TableCell>Has DB Entry</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.error_records?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.row_number}</TableCell>
                    <TableCell>{record.group}</TableCell>
                    <TableCell>{record.subscription_no}</TableCell>
                    <TableCell>{record.amount ? `₹${record.amount}` : '-'}</TableCell>
                    <TableCell>{record.transaction_date || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={record.error_source || 'N/A'} 
                        size="small" 
                        color={record.error_source === 'CSV' ? 'info' : record.error_source === 'DB' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={record.has_db_entry ? 'Yes' : 'No'} 
                        size="small" 
                        color={record.has_db_entry ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={record.status} 
                        color={getStatusColor(record.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{record.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
};

export default VerificationResults;
